import React from 'react';
import { ChevronDown, Search, Check } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../../ui/popover';

// Searchable multi-select used by the analytics filter bar.
export default function MultiPicker({ label, options, value, onChange, testId }) {
  const [q, setQ] = React.useState('');
  const selected = new Set(value);
  const shown = options.filter((o) => o.label.toLowerCase().includes(q.toLowerCase()));
  const toggle = (k) => {
    const next = new Set(selected);
    next.has(k) ? next.delete(k) : next.add(k);
    onChange([...next]);
  };
  const summary = value.length === 0 ? `All ${label.toLowerCase()}` : value.length === 1
    ? (options.find((o) => o.key === value[0])?.label || '1 selected') : `${value.length} selected`;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`h-9 w-full flex items-center justify-between gap-2 px-3 rounded-lg border text-sm bg-white transition-colors hover:border-fuchsia-300 ${value.length ? 'border-fuchsia-400 text-fuchsia-800' : 'border-slate-200 text-slate-700'}`}
          data-testid={testId}
        >
          <span className="truncate">{summary}</span>
          <ChevronDown className="w-3.5 h-3.5 shrink-0 opacity-60" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-0 bg-white" data-testid={`${testId}-menu`}>
        <div className="p-2 border-b border-slate-100 relative">
          <Search className="w-3.5 h-3.5 absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Search ${label.toLowerCase()}…`}
            className="w-full h-8 pl-7 pr-2 text-sm rounded-md bg-slate-50 outline-none focus:ring-1 focus:ring-fuchsia-300" data-testid={`${testId}-search`} />
        </div>
        <ul className="max-h-60 overflow-y-auto py-1">
          {shown.length === 0 && <li className="px-3 py-2 text-xs text-slate-500">No matches</li>}
          {shown.map((o) => (
            <li key={o.key}>
              <button type="button" onClick={() => toggle(o.key)}
                className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-left hover:bg-fuchsia-50"
                data-testid={`${testId}-option-${String(o.key).replace(/[^a-zA-Z0-9]+/g, '-').toLowerCase()}`}>
                <span className={`w-4 h-4 rounded border flex items-center justify-center ${selected.has(o.key) ? 'bg-fuchsia-600 border-fuchsia-600' : 'border-slate-300'}`}>
                  {selected.has(o.key) && <Check className="w-3 h-3 text-white" />}
                </span>
                <span className="truncate">{o.label}</span>
              </button>
            </li>
          ))}
        </ul>
        {value.length > 0 && (
          <button type="button" onClick={() => onChange([])} className="w-full text-xs font-medium text-fuchsia-700 py-2 border-t border-slate-100 hover:bg-fuchsia-50" data-testid={`${testId}-clear`}>
            Clear selection
          </button>
        )}
      </PopoverContent>
    </Popover>
  );
}
